import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState } from 'react';
import { api, ApiError } from '../utils/api.js';
import { getAccounts, switchActiveAccount, upsertAccountSession, removeActiveAccountSession, clearAllAccountSessions, } from '../utils/accounts.js';
const Ctx = createContext(null);
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [accounts, setAccountsState] = useState(() => getAccounts());
    const refreshAccounts = () => setAccountsState(getAccounts());
    async function refresh() {
        setLoading(true);
        try {
            const u = await api.get('/auth/me');
            setUser(u);
            setError(null);
        }
        catch (err) {
            if (err instanceof ApiError && err.status === 401) {
                setUser(null);
            }
            else {
                setError(err.message);
            }
        }
        finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        void refresh();
    }, []);
    /**
     * Login flow: POST credentials to the BFF's /api/auth/login on our
     * own origin. The BFF proxies to RAINDB_APP_URL (server-side env;
     * always reachable from where the BFF runs), gets the JWT back,
     * stamps the httpOnly fdn_token cookie, and returns { token, user }.
     *
     * Earlier this code POSTed directly to http://localhost:4000/auth/login
     * from the browser. That worked in dev (where the developer runs
     * raindb-app on :4000) but in production it caused the browser to
     * hit the USER's local machine instead of the production raindb-app
     * -- triggering Chrome 143's Local Network Access prompt and
     * authenticating against whatever local raindb-app the user happened
     * to have running. The local JWT was signed with a different
     * JWT_SECRET than production, so every subsequent /api request came
     * back 401 ("Session expired or revoked").
     *
     * Same-origin /api/auth/login keeps the browser away from the user's
     * private network entirely.
     */
    async function login(email, password) {
        setLoading(true);
        setError(null);
        try {
            const r = await api.post('/auth/login', { email, password });
            // Save in the multi-account store + mark active. The Bearer
            // token gets layered onto every subsequent request via api.ts.
            // The httpOnly cookie was set by the BFF in the same response
            // so SSE/uploads that don't read localStorage still work.
            upsertAccountSession({
                token: r.token,
                userId: r.user.userId,
                email: r.user.email,
                name: r.user.name,
            });
            refreshAccounts();
            setUser(r.user);
        }
        catch (err) {
            setError(err.message);
            throw err;
        }
        finally {
            setLoading(false);
        }
    }
    async function register(email, password, name) {
        setLoading(true);
        setError(null);
        try {
            const r = await api.post('/auth/register', { email, password, name });
            upsertAccountSession({
                token: r.token,
                userId: r.user.userId,
                email: r.user.email,
                name: r.user.name,
            });
            refreshAccounts();
            setUser(r.user);
        }
        catch (err) {
            setError(err.message);
            throw err;
        }
        finally {
            setLoading(false);
        }
    }
    /**
     * Logout the currently-active account. If another saved account is
     * available we keep the user "logged in" by switching to it; the
     * caller-side experience is "the avatar chip is now someone else."
     * If no other accounts remain, falls back to the cookie-only logout
     * which clears the cookie + sets user=null.
     */
    async function logout() {
        await api.post('/auth/logout').catch(() => { });
        const next = removeActiveAccountSession();
        refreshAccounts();
        if (next) {
            // Re-fetch /me with the newly-active token. authHeaders() in
            // api.ts now sends the new Bearer; the BFF's extractToken
            // takes precedence over any leftover cookie.
            await refresh();
        }
        else {
            setUser(null);
        }
    }
    /**
     * Wipe ALL saved accounts. Dev "I'm done testing" button.
     */
    async function logoutAll() {
        await api.post('/auth/logout').catch(() => { });
        clearAllAccountSessions();
        refreshAccounts();
        setUser(null);
    }
    /**
     * Switch the active account to one already saved. The Bearer token
     * stored by switchActiveAccount becomes the next request's auth.
     */
    async function switchAccount(accountId) {
        const account = switchActiveAccount(accountId);
        if (!account)
            return;
        refreshAccounts();
        await refresh();
    }
    return (_jsx(Ctx.Provider, { value: {
            user, loading, error,
            login, register, logout, logoutAll, refresh,
            accounts, switchAccount,
        }, children: children }));
}
export function useAuth() {
    const ctx = useContext(Ctx);
    if (!ctx)
        throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
